import axios from 'axios';
import { toast } from 'react-toastify';

const baseURL = process.env.REACT_APP_API_BASE_URL;
export const USE_TOKEN_AUTH = !(process.env.REACT_APP_USE_COOKIE === 'true');

const authChannel = new BroadcastChannel('auth-channel');

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';


export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

const api = axios.create({
  baseURL,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    if (USE_TOKEN_AUTH) {
      const token = tokenStorage.getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let failedQueue = [];
let isRefreshing = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (USE_TOKEN_AUTH && token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentRefreshToken = USE_TOKEN_AUTH ? tokenStorage.getRefreshToken() : null;

        if (USE_TOKEN_AUTH && !currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        const refreshPayload = USE_TOKEN_AUTH ? { refreshToken: currentRefreshToken } : {};
        const response = await axios.post(`${baseURL}/api/auth/refresh`, refreshPayload, {
          withCredentials: true,
        });

        const newAccessToken = response.data?.payload?.accessToken;
        const newRefreshToken = response.data?.payload?.refreshToken;

        if (USE_TOKEN_AUTH) {
          tokenStorage.setTokens(newAccessToken, newRefreshToken);
        }

        authChannel.postMessage({
          type: 'AUTH_REFRESHED',
          user: response.data?.payload?.user,
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        });

        processQueue(null, newAccessToken);
        isRefreshing = false;

        if (USE_TOKEN_AUTH && newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // 1. Properly reject waiting promises so UI callers don't hang
        processQueue(refreshError, null);
        isRefreshing = false;

        const currentRefreshToken = USE_TOKEN_AUTH ? tokenStorage.getRefreshToken() : null;

        // 2. Clear client storage
        if (USE_TOKEN_AUTH) {
          tokenStorage.clearTokens();
        }

        // 3. Notify backend regardless of auth mode
        await axios
          .post(
            `${baseURL}/api/auth/logout`,
            USE_TOKEN_AUTH ? { refreshToken: currentRefreshToken } : {},
            { withCredentials: true }
          )
          .catch(() => {});

        toast.error('Session Expired. Try Login again');
        // authChannel.postMessage({ type: 'LOGOUT' });

        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);


export default api;